import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ReplyIcon from '@mui/icons-material/Reply';
import type { FHRequestMessage, TimelineItem } from '../types';
import type { Email } from '../api/email_api';
import type { AssociatedFileInfo } from '../api/file_association_api';
import { SafeHtml } from './SafeHtml';
import { MessageAttachmentChips } from './MessageAttachmentChips';
import { DelayEventRow } from './DelayEventRow';

interface MessageListProps {
  items: TimelineItem[];
  perspective: 'client' | 'assistant';
  /** Map of message UUID -> attachments for that message */
  attachmentsByMessage?: Record<string, AssociatedFileInfo[]>;
  /** Callback when user wants to reply to an incoming email */
  onReplyToIncomingEmail?: (message: FHRequestMessage) => void;
}

// --- Message helpers ---

function isIncomingEmail(message: FHRequestMessage): boolean {
  return !!message.sender;
}

function getSenderLabel(message: FHRequestMessage) {
  const { message_type, author, author_name } = message;

  if (message_type === 'assistant') {
    if (author === 'manager') {
      return author_name ? `${author_name} (Internal)` : 'Manager (Internal)';
    }
    return author_name ? `${author_name} (Internal)` : 'Assistant (Internal)';
  }

  switch (message_type) {
    case 'client':
      if (isIncomingEmail(message)) {
        return message.sender || author_name || 'Customer';
      }
      return author_name || 'Customer';
    case 'helper':
      return author_name || 'Assistant';
    case 'third-party':
      return message.sender || author_name || 'Third Party';
    case 'mentor':
      return 'Support';
    case 'system':
      return 'System';
    default:
      return 'Unknown';
  }
}

function getMessageBorderColor(message: FHRequestMessage, perspective: 'client' | 'assistant') {
  const { message_type, author } = message;

  if (message_type === 'assistant') {
    return author === 'manager' ? 'success.main' : 'warning.main';
  }
  if (message_type === 'third-party') return 'secondary.dark';
  if (message_type === 'system' || message_type === 'mentor') return 'text.disabled';

  const isOwnMessage =
    perspective === 'client'
      ? message_type === 'client'
      : message_type === 'helper';

  const isOtherParty =
    perspective === 'client'
      ? message_type === 'helper'
      : message_type === 'client';

  if (isOwnMessage) return 'primary.main';
  if (isOtherParty) return 'secondary.main';
  return 'text.disabled';
}

function getMessageTypeChip(message: FHRequestMessage): { label: string; color: 'default' | 'warning' | 'success' | 'secondary' } | null {
  if (message.message_type === 'assistant') return { label: 'Internal', color: 'warning' };
  if (message.message_type === 'system') return { label: 'System', color: 'default' };
  if (message.message_type === 'mentor') return { label: 'Mentor', color: 'default' };
  if (isIncomingEmail(message)) return { label: 'Incoming Email', color: 'secondary' };
  if (message.message_type === 'third-party') return { label: 'Incoming Email', color: 'secondary' };
  return null;
}

// --- Email status chip color ---

function getEmailStatusColor(status: string): 'default' | 'success' | 'primary' | 'error' {
  switch (status) {
    case 'delivered': return 'success';
    case 'opened':
    case 'clicked': return 'primary';
    case 'bounced':
    case 'dropped': return 'error';
    default: return 'default';
  }
}

// --- Visibility filter ---

function isItemVisible(item: TimelineItem, perspective: 'client' | 'assistant'): boolean {
  if (item.kind === 'outgoing_email') {
    return perspective === 'assistant';
  }
  if (item.kind === 'delay_event') {
    // Delay events are only visible to assistants
    return perspective === 'assistant';
  }
  const message = item.data;
  if (message.message_type === 'assistant') {
    return perspective === 'assistant';
  }
  if (perspective === 'client') {
    return message.client_viewable;
  }
  return true;
}

// --- Component ---

export function MessageList({ items, perspective, attachmentsByMessage, onReplyToIncomingEmail }: MessageListProps) {
  const visibleItems = items.filter((item) => isItemVisible(item, perspective));

  // Helper to get unique key for each item type
  const getItemKey = (item: TimelineItem, index: number): string => {
    if (item.kind === 'message') return item.data.key || `msg-${index}`;
    if (item.kind === 'outgoing_email') return item.data.uuid;
    if (item.kind === 'delay_event') return item.data.uuid;
    return `item-${index}`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      {visibleItems.map((item, index) => (
        <Box key={getItemKey(item, index)}>
          {index > 0 && <Divider sx={{ my: 1.5 }} />}
          {item.kind === 'message' && (
            <MessageRow
              message={item.data}
              perspective={perspective}
              attachments={attachmentsByMessage?.[item.data.uuid]}
              onReply={onReplyToIncomingEmail}
            />
          )}
          {item.kind === 'outgoing_email' && (
            <OutgoingEmailRow email={item.data} />
          )}
          {item.kind === 'delay_event' && (
            <DelayEventRow event={item.data} />
          )}
        </Box>
      ))}
    </Box>
  );
}

// --- Message row ---

function MessageRow({
  message,
  perspective,
  attachments,
  onReply,
}: {
  message: FHRequestMessage;
  perspective: 'client' | 'assistant';
  attachments?: AssociatedFileInfo[];
  onReply?: (message: FHRequestMessage) => void;
}) {
  const borderColor = getMessageBorderColor(message, perspective);
  const chip = getMessageTypeChip(message);
  const canReply = perspective === 'assistant' && (isIncomingEmail(message) || message.message_type === 'third-party');

  // Debug logging
  if (message.message_type === 'third-party' || message.sender) {
    console.log('Reply button debug:', {
      sender: message.sender,
      message_type: message.message_type,
      perspective,
      isIncomingEmail: isIncomingEmail(message),
      canReply,
      hasOnReply: !!onReply,
    });
  }

  return (
    <Box
      sx={{
        pl: 2,
        borderLeft: 3,
        borderColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: borderColor, fontWeight: 500 }}>
          {getSenderLabel(message)}
        </Typography>
        {chip && (
          <Chip label={chip.label} color={chip.color} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {new Date(message.date_created).toLocaleString()}
        </Typography>
        {canReply && onReply && (
          <Button
            size="small"
            onClick={() => onReply(message)}
            title="Reply via email"
            startIcon={<ReplyIcon fontSize="small" />}
            sx={{ ml: 'auto' }}
          >
            Reply
          </Button>
        )}
      </Box>

      {/* Third-party email metadata */}
      {(message.message_type === 'third-party' || isIncomingEmail(message)) && (message.subject || message.original_to) && (
        <Box sx={{ mt: 0.5 }}>
          {message.subject && (
            <Typography variant="caption" color="text.secondary" display="block">
              Subject: {message.subject}
            </Typography>
          )}
          {message.original_to && (
            <Typography variant="caption" color="text.secondary" display="block">
              To: {message.original_to}
            </Typography>
          )}
          {message.original_cc && (
            <Typography variant="caption" color="text.secondary" display="block">
              CC: {message.original_cc}
            </Typography>
          )}
        </Box>
      )}

      {message.content_type === 'text/html' ? (
        <SafeHtml content={message.content} forceLight />
      ) : (
        <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
          {message.content}
        </Typography>
      )}
      {attachments && <MessageAttachmentChips attachments={attachments} />}
    </Box>
  );
}

// --- Outgoing email row ---

function OutgoingEmailRow({ email }: { email: Email }) {
  return (
    <Box
      sx={{
        pl: 2,
        borderLeft: 3,
        borderColor: 'info.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <EmailIcon sx={{ fontSize: 14, color: 'info.main' }} />
        <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 500 }}>
          Email to {email.to_email}
        </Typography>
        <Chip label="Outgoing Email" color="info" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        <Chip
          label={email.status}
          color={getEmailStatusColor(email.status)}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {new Date(email.date_created).toLocaleString()}
        </Typography>
      </Box>

      {email.subject && (
        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
          {email.subject}
        </Typography>
      )}

      <Typography
        sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
        variant="body2"
        color="text.secondary"
      >
        {email.content.length > 300 ? email.content.slice(0, 300) + '...' : email.content}
      </Typography>
    </Box>
  );
}
